import { gql, type TypedDocumentNode } from "@apollo/client";
import type {
  IMyPromoterRewardsSummary,
  IPromoterIncentive,
  IPromoterPerformance,
  IRewardTier,
  IRewardTransaction,
} from "@ticketon/rewards-core";
import {
  MONEY_FIELDS,
  PAGE_FIELDS,
  PROMOTER_INCENTIVE_FIELDS,
  REWARD_BALANCE_FIELDS,
  TIER_FIELDS,
  TIER_SUMMARY_FIELDS,
  TRANSACTION_FIELDS,
} from "./fragments";
import type { IConnection, IPageVariables } from "../pagination";

/** GraphQL `MyPromoterRewardsSummary`. Alias of the `@ticketon/rewards-core` model. */
export type IPromoterRewardsSummary = IMyPromoterRewardsSummary;

const PERFORMANCE_BREAKDOWN_FIELDS = `
      key
      label
      grossSales {
        ${MONEY_FIELDS}
      }
      netSales {
        ${MONEY_FIELDS}
      }
      ticketsSold
      refunds {
        ${MONEY_FIELDS}
      }
      pointsEarned`;

/** `PromoterPerformance` has no id: it is a computed, period-scoped value embedded in its parent. */
const PROMOTER_PERFORMANCE_FIELDS = gql`
  ${TIER_SUMMARY_FIELDS}
  fragment PromoterPerformanceFields on PromoterPerformance {
    promoterId
    programId
    periodStart
    periodEnd
    grossSales {
      ${MONEY_FIELDS}
    }
    netSales {
      ${MONEY_FIELDS}
    }
    ticketsSold
    eventsSold
    refunds {
      ${MONEY_FIELDS}
    }
    chargebacks {
      ${MONEY_FIELDS}
    }
    qualifyingRevenue {
      ${MONEY_FIELDS}
    }
    pointsEarned
    progressToNextTier
    salesToNextTier {
      ${MONEY_FIELDS}
    }
    currentTier {
      ...TierSummaryFields
    }
    nextTier {
      ...TierSummaryFields
    }
    byEvent {${PERFORMANCE_BREAKDOWN_FIELDS}
    }
    byChannel {${PERFORMANCE_BREAKDOWN_FIELDS}
    }
  }
`;

export const GET_MY_PROMOTER_REWARDS_SUMMARY: TypedDocumentNode<
  { myPromoterRewardsSummary: IPromoterRewardsSummary },
  Record<string, never>
> = gql`
  ${REWARD_BALANCE_FIELDS}
  ${PROMOTER_PERFORMANCE_FIELDS}
  ${PROMOTER_INCENTIVE_FIELDS}
  query GetMyPromoterRewardsSummary {
    myPromoterRewardsSummary {
      id
      balance {
        ...RewardBalanceFields
      }
      performance {
        ...PromoterPerformanceFields
      }
      incentives {
        ...PromoterIncentiveFields
      }
    }
  }
`;

export const GET_MY_PROMOTER_PERFORMANCE: TypedDocumentNode<
  { myPromoterPerformance: IPromoterPerformance },
  { periodStart?: string; periodEnd?: string }
> = gql`
  ${PROMOTER_PERFORMANCE_FIELDS}
  query GetMyPromoterPerformance($periodStart: String, $periodEnd: String) {
    myPromoterPerformance(periodStart: $periodStart, periodEnd: $periodEnd) {
      ...PromoterPerformanceFields
    }
  }
`;

export const GET_MY_PROMOTER_REWARD_TRANSACTIONS: TypedDocumentNode<
  { myPromoterRewardTransactions: IConnection<IRewardTransaction> },
  IPageVariables
> = gql`
  ${TRANSACTION_FIELDS}
  query GetMyPromoterRewardTransactions($pageNumber: Int!, $pageSize: Int!) {
    myPromoterRewardTransactions(pageNumber: $pageNumber, pageSize: $pageSize) {
      items {
        ...RewardTransactionFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_MY_PROMOTER_REWARD_TIERS: TypedDocumentNode<
  { myPromoterRewardTiers: IRewardTier[] },
  Record<string, never>
> = gql`
  ${TIER_FIELDS}
  query GetMyPromoterRewardTiers {
    myPromoterRewardTiers {
      ...RewardTierFields
    }
  }
`;

export const GET_MY_PROMOTER_ACTIVE_INCENTIVES: TypedDocumentNode<
  { myPromoterActiveIncentives: IPromoterIncentive[] },
  Record<string, never>
> = gql`
  ${PROMOTER_INCENTIVE_FIELDS}
  query GetMyPromoterActiveIncentives {
    myPromoterActiveIncentives {
      ...PromoterIncentiveFields
    }
  }
`;
